(() => {
  const dialog = document.getElementById('DiamondVisualiser');
  if (!dialog || dialog.dataset.initialised) return;
  dialog.dataset.initialised = 'true';
  let source;
  let referencePromise;
  let loading;
  let ready = false;
  let opener;
  let scrollState;
  let frame;
  let startedOnBackdrop = false;
  let state;
  let renderedTone;
  let renderedShape;
  let focusMode = 'pointer';
  const mobileLayout = window.matchMedia('(max-width: 749px)');
  const schemeProperties = [
    '--color-background', '--color-background-rgb', '--color-foreground', '--color-foreground-rgb',
    '--color-foreground-heading', '--color-border', '--color-border-rgb', '--color-shadow',
    '--color-primary-button-background', '--color-primary-button-text', '--color-primary-button-border',
    '--color-primary-button-hover-background', '--color-primary-button-hover-text', '--color-primary-button-hover-border',
    '--color-secondary-button-background', '--color-secondary-button-text', '--color-secondary-button-border',
    '--color-secondary-button-hover-background', '--color-secondary-button-hover-text', '--color-secondary-button-hover-border',
    '--font-body--family', '--font-body--weight', '--font-paragraph--size', '--font-paragraph--line-height',
    '--font-h1--family', '--font-h1--size', '--font-h1--weight', '--font-h1--line-height',
    '--font-h2--family', '--font-h2--size', '--font-h2--weight', '--font-h2--line-height',
    '--button-font-family-primary', '--button-font-size-primary', '--button-font-weight-primary', '--button-text-case-primary',
    '--button-font-family-secondary', '--button-font-size-secondary', '--button-font-weight-secondary', '--button-text-case-secondary'
  ];
  const find = selector => dialog.querySelector(selector);
  const all = selector => [...dialog.querySelectorAll(selector)];
  const saveProperty = (element, name) => ({ element, name, value: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name) });
  const restoreProperty = ({ element, name, value, priority }) => value ? element.style.setProperty(name, value, priority) : element.style.removeProperty(name);

  function lockScroll() {
    const body = document.body;
    const root = document.documentElement;
    const properties = ['position','top','left','right','width','overflow','padding-right'].map(name => saveProperty(body, name));
    properties.push(saveProperty(root, 'overflow'));
    scrollState = { x: window.scrollX, y: window.scrollY, properties };
    const scrollbar = window.innerWidth - root.clientWidth;
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.setProperty('position', 'fixed', 'important');
    body.style.setProperty('top', `${-scrollState.y}px`, 'important');
    body.style.setProperty('left', `${-scrollState.x}px`, 'important');
    body.style.setProperty('right', '0', 'important');
    body.style.setProperty('width', '100%', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');
    body.style.setProperty('padding-right', `${padding + scrollbar}px`, 'important');
    root.style.setProperty('overflow', 'hidden', 'important');
  }

  function unlockScroll() {
    if (!scrollState) return;
    const previous = scrollState;
    scrollState = undefined;
    const behavior = saveProperty(document.documentElement, 'scroll-behavior');
    document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    previous.properties.forEach(restoreProperty);
    window.scrollTo(previous.x, previous.y);
    restoreProperty(behavior);
  }

  function applyScheme(button) {
    const computed = getComputedStyle(button);
    const values = schemeProperties.map(name => [name, computed.getPropertyValue(name).trim()]);
    values.forEach(([name, value]) => {
      if (value) dialog.style.setProperty(name, value);
      else dialog.style.removeProperty(name);
    });
  }

  function prepareContent() {
    if (!find('.dv__frame')) dialog.append(find('[data-dv-template]').content.cloneNode(true));
    syncLayout();
  }

  function syncLayout() {
    const weight = find('.dv__weight');
    if (!weight) return;
    const active = weight.contains(document.activeElement) ? document.activeElement : undefined;
    if (mobileLayout.matches) {
      const slot = find('[data-dv-mobile-weight-slot]');
      if (weight.parentElement !== slot) slot.append(weight);
    } else {
      const shapes = find('[data-dv-shapes]');
      if (weight.nextElementSibling !== shapes) shapes.before(weight);
    }
    if (active && document.activeElement !== active) active.focus({ preventScroll: true });
  }

  mobileLayout.addEventListener('change', syncLayout);

  function loadReference() {
    if (!referencePromise) {
      referencePromise = import(new URL(dialog.dataset.referenceUrl, document.baseURI).href).catch(error => {
        referencePromise = undefined;
        throw error;
      });
    }
    return referencePromise;
  }

  function warm() {
    if (!document.querySelector('[data-diamond-visualiser-open]')) return;
    prepareContent();
    initialise();
  }

  function close() {
    if (dialog.open) {
      dialog.close();
      restorePage();
    }
  }

  function restorePage() {
    if (dialog.open || !scrollState) return;
    unlockScroll();
    if (opener?.isConnected) {
      opener.classList.toggle('dv__pointer-focus', focusMode === 'pointer');
      opener.focus({ preventScroll: true });
    }
  }

  function render() {
    if (!ready) return;
    const [length, width] = source.dimensionsAt(state.shape, state.carat);
    const unit = source.reference.unitsPerMm;
    const w = width * unit;
    const h = length * unit;
    const diamond = find('[data-dv-diamond]');
    const selectedShape = find(`[name="dv-shape"][value="${state.shape}"]`);
    if (renderedShape !== state.shape) {
      diamond.setAttribute('href', selectedShape.dataset.artwork);
      renderedShape = state.shape;
    }
    diamond.setAttribute('x', -w / 2);
    diamond.setAttribute('y', -h / 2);
    diamond.setAttribute('width', w);
    diamond.setAttribute('height', h);
    const prongs = {
      round: [[-.34,-.34],[.34,-.34],[-.34,.34],[.34,.34]],
      oval: [[-.34,-.35],[.34,-.35],[-.34,.35],[.34,.35]],
      emerald: [[-.43,-.43],[.43,-.43],[-.43,.43],[.43,.43]],
      pear: [[0,-.48],[-.43,.16],[.43,.16],[0,.48]],
      cushion: [[-.40,-.40],[.40,-.40],[-.40,.40],[.40,.40]],
      princess: [[-.48,-.48],[.48,-.48],[-.48,.48],[.48,.48]],
      radiant: [[-.43,-.43],[.43,-.43],[-.43,.43],[.43,.43]],
      marquise: [[0,-.48],[0,.48],[-.46,0],[.46,0]],
      asscher: [[-.35,-.35],[.35,-.35],[-.35,.35],[.35,.35]],
      heart: [[-.4,-.22],[.4,-.22],[-.09,.43],[.09,.43]]
    }[state.shape];
    all('[data-dv-prongs] circle').forEach((circle, index) => {
      circle.setAttribute('cx', prongs[index][0] * w);
      circle.setAttribute('cy', prongs[index][1] * h);
    });
    const weight = state.carat.toFixed(2);
    find('[data-dv-weight]').textContent = weight;
    const dimensions = find('[data-dv-dimensions]');
    dimensions.textContent = state.shape === 'round'
      ? dimensions.dataset.round.replace('__VALUE__', width.toFixed(2))
      : dimensions.dataset.other.replace('__LENGTH__', length.toFixed(2)).replace('__WIDTH__', width.toFixed(2));
    const slider = find('[data-dv-slider]');
    slider.setAttribute('aria-valuetext', slider.dataset.valueText.replace('__VALUE__', weight));
    slider.style.setProperty('--dv-progress', `${(state.carat - source.settings.min) / (source.settings.max - source.settings.min) * 100}%`);
    if (renderedTone !== state.tone) {
      const tone = source.tones[state.tone];
      all('#dv-skin feComponentTransfer > *').forEach((channel, index) => {
        const [slope, intercept] = tone.channels[index];
        if (tone.gamma) {
          channel.setAttribute('type', 'gamma');
          channel.setAttribute('amplitude', slope);
          channel.setAttribute('exponent', tone.gamma[index]);
          channel.setAttribute('offset', intercept);
          channel.removeAttribute('slope');
          channel.removeAttribute('intercept');
        } else {
          channel.setAttribute('type', 'linear');
          channel.setAttribute('slope', slope);
          channel.setAttribute('intercept', intercept);
          channel.removeAttribute('amplitude');
          channel.removeAttribute('exponent');
          channel.removeAttribute('offset');
        }
      });
      renderedTone = state.tone;
    }
    find('#DiamondVisualiserImageTitle').textContent = `${selectedShape.closest('label').textContent.trim()} · ${slider.getAttribute('aria-valuetext')} · ${dimensions.textContent} · ${find('.dv__ring-size').textContent}`;
  }

  async function initialise() {
    if (ready) return;
    if (loading) return loading;
    loading = (async () => {
      source = await loadReference();
      source.validateSettings();
      state = { shape: source.settings.shape, carat: source.settings.start, tone: source.settings.tone };
      const slider = find('[data-dv-slider]');
      slider.min = source.settings.min;
      slider.max = source.settings.max;
      slider.step = source.settings.step;
      slider.value = state.carat;
      find('[data-dv-min]').textContent = Number(slider.min).toFixed(2);
      find('[data-dv-max]').textContent = Number(slider.max).toFixed(2);
      find(`[name="dv-shape"][value="${state.shape}"]`).checked = true;
      find(`[name="dv-tone"][value="${state.tone}"]`).checked = true;
      source.tones.forEach((tone, index) => find(`[name="dv-tone"][value="${index}"]`).closest('label').style.setProperty('--dv-swatch', tone.swatch));
      const { centre, rotation, bandWidthMm, unitsPerMm } = source.reference;
      find('[data-dv-ring]').setAttribute('transform', `translate(${centre[0]} ${centre[1]}) rotate(${rotation})`);
      const bands = all('[data-dv-ring] > path');
      bands[0].setAttribute('stroke-width', bandWidthMm * unitsPerMm + 2);
      bands[1].setAttribute('stroke-width', bandWidthMm * unitsPerMm);
      ready = true;
      all('fieldset, [data-dv-slider]').forEach(element => element.disabled = false);
      find('[data-dv-status]').textContent = '';
      render();
    })().catch(error => {
      loading = undefined;
      const status = find('[data-dv-status]');
      status.textContent = status.dataset.error;
      console.error('Diamond visualiser could not load:', error);
    });
    return loading;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-diamond-visualiser-open]');
    if (!button || dialog.open) return;
    prepareContent();
    opener = button;
    applyScheme(button);
    dialog.showModal();
    lockScroll();
    find('[data-dv-close]').focus({ preventScroll: true });
    initialise();
  });

  document.addEventListener('pointerdown', () => { focusMode = 'pointer'; }, { passive: true, capture: true });
  document.addEventListener('keydown', event => {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    focusMode = 'keyboard';
    document.querySelectorAll('[data-diamond-visualiser-open].dv__pointer-focus').forEach(button => button.classList.remove('dv__pointer-focus'));
  }, { capture: true });

  ['pointerover', 'focusin', 'pointerdown'].forEach(type => document.addEventListener(type, event => {
    if (event.target.closest('[data-diamond-visualiser-open]')) warm();
  }, { passive: true }));
  if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 1500 });
  else window.setTimeout(warm, 500);

  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-dv-close]')) close();
    if (event.target === dialog && startedOnBackdrop && outside(event)) close();
    startedOnBackdrop = false;
  });
  const outside = event => {
    const bounds = dialog.getBoundingClientRect();
    return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
  };
  dialog.addEventListener('pointerdown', event => { startedOnBackdrop = event.target === dialog && outside(event); });
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('close', restorePage);
  dialog.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const controls = all('button:not(:disabled), input[type="range"]:not(:disabled), input[type="radio"]:checked').filter(element => !element.closest('fieldset:disabled'));
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  dialog.addEventListener('input', event => {
    if (!ready) return;
    if (event.target.matches('[data-dv-slider]')) state.carat = Number(event.target.value);
    else if (event.target.name === 'dv-shape') state.shape = event.target.value;
    else if (event.target.name === 'dv-tone') state.tone = Number(event.target.value);
    else return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  });
  window.addEventListener('pagehide', close);
})();
